import { Parent } from "@/models";
import { AccessibleSchoolQueryParam, RemoteTeachingService, ScheduleParam, FetchSessionDtoResModel, TeacherService } from "@/services";
import { Logger } from "@/utils/logger";
import { All } from "@/views/teacher-calendar/teacher-calendar";
import { ActionContext, ActionTree } from "vuex";
import { TeacherState } from "./state";
import { defineVuexName, vuexName, MODULES } from "@/store/utils";

const actions: ActionTree<TeacherState, any> = {
  //setInfo
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.SET_INFO)]({ dispatch, commit }, payload: Parent) {
    await dispatch(vuexName(MODULES.TEACHER.DISPATCHES.SET_ACCEPT_POLICY, false));
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_INFO, false), payload);
  },
  //loadAllClassesSchedulesAllSchool
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.LOAD_ALL_CLASSES_SCHEDULES_ALL_SCHOOL)]({ commit }: ActionContext<TeacherState, any>) {
    const response = await TeacherService.getAllClassesOfAllSchools();
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_CLASSES_SCHEDULES_ALL_SCHOOL, false), response);
  },
  //loadAllClassesSchedules
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.LOAD_ALL_CLASSES_SCHEDULES)](
    { commit }: ActionContext<TeacherState, any>,
    payload: { schoolId: string; deviceId: string },
  ) {
    const response = await TeacherService.getAllClassesSchedule(payload.schoolId);
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_CLASSES_SCHEDULES, false), response);
  },
  //loadSchedules
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.LOAD_SCHEDULES)](
    { commit, state }: ActionContext<TeacherState, any>,
    payload: {
      schoolId: string;
      classId: string;
      groupId: string;
      startDate: string;
      endDate: string;
    },
  ) {
    if (!state.info) return;
    const response = await TeacherService.getScheduleCalendar(
      payload.schoolId === All ? "" : payload.schoolId,
      payload.classId,
      payload.groupId,
      payload.startDate,
      payload.endDate,
    );
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_CALENDAR_SCHEDULES, false), response);
  },
  //loadAllSchedules
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.LOAD_ALL_SCHEDULES)](
    { commit }: ActionContext<TeacherState, any>,
    payload: { startDate: string; endDate?: string },
  ) {
    const response = await TeacherService.getAllScheduleCalendar(payload.startDate, payload.endDate ?? "");
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_CALENDAR_SCHEDULES, false), response);
  },
  //skipSchedule
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.SKIP_SCHEDULE)](
    { commit }: ActionContext<TeacherState, any>,
    payload: { day: string; customId: string; data: ScheduleParam },
  ) {
    const response = await TeacherService.skipSchedule(payload.data);
    if (response)
      commit(vuexName(MODULES.TEACHER.COMMITS.UPDATE_CALENDAR_SCHEDULE, false), {
        ...payload,
        scheduleId: response.scheduleId,
      });
  },
  //createSchedule
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.CREATE_SCHEDULE)](
    { commit }: ActionContext<TeacherState, any>,
    payload: {
      day: string;
      className: string;
      groupName: string;
      data: ScheduleParam;
      id?: string;
    },
  ) {
    const response = await TeacherService.createSchedule(payload.data);
    if (response) {
      payload.id = response;
      commit(vuexName(MODULES.TEACHER.COMMITS.UPDATE_CALENDAR_SCHEDULE, false), payload);
    }
  },
  //updateSchedule
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.UPDATE_SCHEDULE)](
    { commit }: ActionContext<TeacherState, any>,
    payload: { day: string; groupName: string; data: ScheduleParam },
  ) {
    const response = await TeacherService.updateSchedule(payload.data);
    if (response) commit(vuexName(MODULES.TEACHER.COMMITS.UPDATE_CALENDAR_SCHEDULE, false), payload);
  },
  //deleteSchedule
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.DELETE_SCHEDULE)](
    { commit }: ActionContext<TeacherState, any>,
    payload: { day: string; data: { scheduleId: string; type: string } },
  ) {
    const response = await TeacherService.deleteSchedule(payload.data.scheduleId);
    if (response) commit(vuexName(MODULES.TEACHER.COMMITS.UPDATE_CALENDAR_SCHEDULE, false), payload);
  },
  //loadAccessibleSchools
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.LOAD_ACCESSIBLE_SCHOOLS)](
    { commit }: ActionContext<TeacherState, any>,
    payload: AccessibleSchoolQueryParam,
  ) {
    try {
      const response = await TeacherService.getAccessibleSchools(payload);
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_SCHOOLS, false), response);
    } catch (error) {
      Logger.error(error);
    }
  },
  //setAcceptPolicy
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.SET_ACCEPT_POLICY)]({ commit }) {
    const policyResponse: FetchSessionDtoResModel = await RemoteTeachingService.acceptPolicy("teacher");
    commit(vuexName(MODULES.TEACHER.COMMITS.SET_ACCEPT_POLICY, false), policyResponse.data);
  },
  //setClassGroup
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.SET_CLASS_GROUP)]({ commit }) {
    const response = await TeacherService.getClassGroup();
    if (response.length) {
      response.forEach((item) => {
        if (item.groups.length) {
          item.groups.sort((a, b) => (a.groupName > b.groupName ? 1 : -1));
        }
      });
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_CLASS_GROUP, false), response);
    }
  },
  async [defineVuexName(MODULES.TEACHER.DISPATCHES.UPDATE_ONGOING_SESSION)]({ commit }) {
    try {
      const resp = await RemoteTeachingService.fetchOngoingSession();
      commit(vuexName(MODULES.TEACHER.COMMITS.SET_ONGOING_SESSION, false), resp.data);
    } catch (e) {
      Logger.error(e);
    }
  },
};

export default actions;
